import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import TopNav from "ui/dashboard/topnav";
import { IPSpeakerSearchDataType, WsResDataType } from "lib/types";
import IPSpeakerSearchItem from "ui/ip-speaker/ip-speaker-search-item";
import Modal from "react-modal";
import clsx from "clsx";
import { SvgToastInfo } from "ui/svgs";
import { ConfirmModalStyles, IdentifiedServerError } from "lib/constants";
import { showToast } from "ui/components/common";
import _ from "lodash";

import { useCustomersService } from "pages/dashboard/customers/service";

export default function Page() {
  const { orgid, siteid, nvrid } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const { sendSearchDeviceRequest, sendAddDeviceRequest } =
    useCustomersService();

  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [IPSpeakers, setIPSpeakers] = useState<IPSpeakerSearchDataType[]>([]);
  const [selectIPSpeakers, setSelectIPSpeakers] = useState<
    IPSpeakerSearchDataType[]
  >([]);
  const [errorSpeakerMacs, setErrorSpeakerMacs] = useState<string[]>([]);
  const [successAddedNum, setSuccessAddedNum] = useState(0);

  const nvrName = state?.nvrName;
  const orgId = orgid!;
  const siteId = siteid!;
  const nvrId = nvrid!;

  useEffect(() => {
    const initSearchDevice = async () => {
      try {
        setLoading(true);
        const res = await sendSearchDeviceRequest(
          { productTypes: ["speaker"], nvrId: nvrId },
          orgId,
        );
        const data = (res as WsResDataType)?.deviceSearchReply?.speakerList;
        setIPSpeakers(data);
      } catch (error) {
        showToast(
          1,
          "There was a problem searching IP speakers. Error:" + error,
        );
      } finally {
        setLoading(false);
      }
    };
    initSearchDevice();
  }, [nvrId]);

  const onSelect = useCallback(
    (IPSpeaker: IPSpeakerSearchDataType, checked: boolean) => {
      if (checked) {
        setSelectIPSpeakers([...selectIPSpeakers, IPSpeaker]);
      } else {
        setSelectIPSpeakers(
          selectIPSpeakers.filter((i) => i.macAddress !== IPSpeaker.macAddress),
        );
      }
    },
    [selectIPSpeakers],
  );

  const submit = async () => {
    try {
      setSaving(true);
      setErrorSpeakerMacs([]);
      setSuccessAddedNum(0);
      const res = await sendAddDeviceRequest(
        +siteId,
        +nvrId,
        selectIPSpeakers,
        orgId,
      );
      const errorSpeakerMacs = [];
      const unAuthSpeakerMacs = [];
      const _IPSpeakers = IPSpeakers;
      let successAddedNum = 0;
      const data = (res as WsResDataType)?.deviceSpeakerAddReply?.data;
      data?.forEach((e) => {
        const index = _IPSpeakers.findIndex(
          (i) => i.macAddress === e.macAddress,
        );
        if (index < 0) {
          return;
        }
        if (e.error) {
          errorSpeakerMacs.push(e.macAddress);
          if (e.error.name === IdentifiedServerError.DeviceUnAuthed) {
            _IPSpeakers[index].status = "un_authed";
            unAuthSpeakerMacs.push(e.macAddress);
          }
        } else {
          successAddedNum++;
          _IPSpeakers[index].status = "added";
          _IPSpeakers[index].oldNvrName = nvrName;
        }
      });
      if (errorSpeakerMacs.length == 0) {
        showToast(0, "The IPSpeakers has been added successfully!");
        navigate(`/dashboard/customers/${orgId}/sites`);
        return;
      }
      const errorWithoutUnAuth = errorSpeakerMacs.filter(
        (e) => !unAuthSpeakerMacs.includes(e),
      );
      setIPSpeakers(_.cloneDeep(_IPSpeakers));
      setErrorSpeakerMacs(errorSpeakerMacs);
      setSuccessAddedNum(successAddedNum);
      setSelectIPSpeakers(
        selectIPSpeakers.filter((e) =>
          errorWithoutUnAuth.includes(e.macAddress),
        ),
      );
    } catch (error) {
      showToast(
        1,
        "There was a problem adding IP speakers. Please try again. Error:" +
          error,
      );
    } finally {
      setModalOpen(false);
      setSaving(false);
    }
  };

  return (
    <>
      <TopNav title="Customer Management" />
      <div className="flex-grow mr-6 mb-6 flex flex-col ">
        <div
          className="w-full flex-grow pt-14 rounded-3xl flex items-center flex-col bg-white overflow-auto"
          style={{ maxHeight: "calc(100vh - 88px)" }}
        >
          <div className="max-w-[600px] w-full">
            <h2 className="mb-8 text-xl font-semibold">
              Add IP Speaker to NVR [{nvrName}]
            </h2>
            {errorSpeakerMacs.length > 0 && (
              <div className="mx-auto w-full">
                <div className="flex items-center justify-center gap-1 mb-3 px-6 py-2 w-full rounded-2xl bg-[#FFEDEB] text-[#F73D2F] font-semibold">
                  <SvgToastInfo width="16" className="fill-[#F73D2F]" />
                  {`${errorSpeakerMacs.length} IP Speaker was failed to be added, ${successAddedNum} succeeded. Please try again.`}
                </div>
              </div>
            )}
            {loading && (
              <div className="text-center w-full">
                <div
                  className="ml-1 text-[#222222] inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                  role="status"
                ></div>
              </div>
            )}
            <div className="mx-auto w-full">
              {IPSpeakers?.map((IPSpeaker, index) => (
                <IPSpeakerSearchItem
                  orgId={orgId}
                  IPSpeakerData={IPSpeaker}
                  onSelect={onSelect}
                  key={index}
                  selected={
                    selectIPSpeakers.findIndex(
                      (e) => e.macAddress === IPSpeaker.macAddress,
                    ) >= 0
                  }
                  hasError={errorSpeakerMacs.includes(IPSpeaker.macAddress)}
                />
              ))}
            </div>
          </div>
          <div className=" w-full bg-white pb-14">
            <div className="max-w-[600px] w-full mt-6 flex mx-auto justify-end ">
              <button
                className="flex justify-center items-center rounded-2xl bg-white px-6 py-[14px] text-base font-semibold text-blue-500 border border-blue-500 transition-colors hover:text-blue-400 hover:border-blue-400 "
                onClick={() => {
                  navigate(`/dashboard/customers/${orgId}/sites`);
                }}
              >
                Back
              </button>
              <button
                className={clsx(
                  "flex justify-center items-center rounded-2xl ml-4 px-7 py-[14px] text-base font-semibold border text-white bg-blue-500 hover:bg-blue-400 transition-colors disabled:bg-blue-300 disabled:hover:bg-blue-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600",
                )}
                disabled={selectIPSpeakers.length == 0}
                onClick={() => {
                  setModalOpen(true);
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={modalOpen}
        style={ConfirmModalStyles}
        contentLabel="Example Modal"
      >
        <h1 className="text-xl font-bold">
          Do you want to add this IP Speaker?
        </h1>
        <div className="text-base mt-4">
          The IP Speaker can be managed in the device list.
        </div>

        <div className="float-right mt-6">
          <button
            disabled={saving}
            className="rounded-2xl bg-white px-6 py-4 border border-blue-500 hover:border-blue-400 text-sm  transition-colors text-blue-500 hover:text-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            onClick={() => {
              setModalOpen(false);
            }}
          >
            Cancel
          </button>
          <button
            disabled={saving}
            className="ml-4 rounded-2xl w-[100px] py-4 bg-blue-500 text-sm font-medium text-white transition-colors hover:bg-blue-400  disabled:bg-blue-300 disabled:hover:bg-blue-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            onClick={submit}
          >
            {saving ? (
              <div
                className="ml-1 text-blue-500  inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                role="status"
              ></div>
            ) : (
              "Confirm"
            )}
          </button>
        </div>
      </Modal>
    </>
  );
}
